/* @flow */
import React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import classnames from 'classnames';

// Components
import { VisibilityTracker } from '../VisibilityTracker/VisibilityTracker';
import { Info } from '../Info';
import { Product } from '../Product';
import QuickViewItem from '../QuickViewItem';
import { TileContextProvider } from '../helpers/TileContext';

// Helpers
import get from 'lodash.get';
import type { Tile_item as TileItemProp } from './__generated__/Tile_item.graphql';
import type { Tile_viewer as TileViewerProp } from './__generated__/Tile_viewer.graphql';
import type { ImageSizeType } from '../types/ImageSizeType';

// Styles
import styles from './styles.scss';

type TileComponentProps = {|
    activeQuickView?: number,
    buyerHost?: string,
    closeQuickView: () => void,
    currency: string,
    dimensions?: string,
    disableLinks?: boolean,
    imageDraggable?: boolean, // undefined draggable will result in default browser behavior
    index: number,
    item: TileItemProp,
    itemsPerRow?: number,
    isMobile?: boolean,
    openQuickView: (arg0?: number) => void,
    showInternalSellerName?: boolean,
    showByLine?: boolean,
    showSellerName?: boolean,
    showCreatorName?: boolean,
    showQuickView?: boolean,
    showQuickViewIcon?: boolean,
    showLightBox?: boolean,
    showPrice?: boolean,
    showTitle?: boolean,
    onClick?: () => void,
    onContentLoaded?: () => void,
    useLoFiLazyLoader?: boolean,
    imageLoadVerticalOffset?: number,
    onContentVisible?: ?() => void,
    srcSetSizes?: string,
    srcSetWidths?: Array<number>,
    srcSetQuality?: number,
    imageSize?: ImageSizeType,
    showMoreOptionsBadge: boolean,
    alignMoreOptionsToBottom?: boolean,
    hideDivider?: boolean,
    hideProductDetails?: boolean,
    hideImagePadding?: boolean,
    priceBold?: boolean,
    titleOneLine?: boolean,
    viewer: TileViewerProp,
|};

class TileComponent extends React.Component<TileComponentProps> {
    constructor() {
        super();
        this.tileRef = React.createRef();
    }

    tileRef: { current: null | HTMLDivElement };

    render() {
        const {
            activeQuickView,
            buyerHost = '',
            closeQuickView,
            currency,
            dimensions,
            disableLinks,
            imageDraggable,
            index,
            item,
            itemsPerRow,
            isMobile,
            openQuickView,
            showInternalSellerName,
            showByLine,
            showSellerName,
            showCreatorName,
            showQuickView,
            showQuickViewIcon,
            showLightBox,
            showPrice,
            showTitle,
            onClick,
            onContentLoaded,
            useLoFiLazyLoader,
            imageLoadVerticalOffset,
            onContentVisible,
            srcSetSizes,
            srcSetWidths,
            srcSetQuality,
            imageSize = 'medium',
            showMoreOptionsBadge,
            alignMoreOptionsToBottom,
            hideDivider,
            hideProductDetails,
            hideImagePadding,
            priceBold,
            titleOneLine,
            viewer,
        } = this.props;

        if (!item) {
            return null;
        }

        const dealerPk = get(item, 'seller.serviceId');
        const seller = get(item, 'seller');
        const isQuickViewActive = index === activeQuickView;
        const displayMoreOptionsBadge = showMoreOptionsBadge
            ? get(item, 'isCustomizable') || get(item, 'isMultiSku')
            : false;

        const sharedProps = {
            item: (item: Object),
            title: get(item, 'title'),
            pdpUrl: buyerHost + get(item, 'localizedPdpUrl'),
            onClick,
            disableLinks,
        };

        const srcSetProps = {
            srcSetSizes,
            srcSetWidths,
            srcSetQuality,
        };

        /* istanbul ignore next */
        const openQuickViewFunc = () => openQuickView(index);

        return (
            <TileContextProvider imageSize={imageSize}>
                <div
                    className={styles.tile}
                    data-pk={item.serviceId}
                    data-dealerpk={dealerPk}
                    data-vertical={item.vertical}
                    ref={this.tileRef}
                    data-tn="item-title"
                >
                    {onContentVisible && (
                        <VisibilityTracker
                            elementRef={this.tileRef}
                            onContentVisible={onContentVisible}
                        />
                    )}
                    <Product
                        {...sharedProps}
                        dimensions={dimensions}
                        index={index}
                        imageDraggable={imageDraggable}
                        itemsPerRow={itemsPerRow}
                        onContentLoaded={onContentLoaded}
                        openQuickView={openQuickViewFunc}
                        showQuickView={showQuickView}
                        showLightBox={showLightBox}
                        useLoFiLazyLoader={useLoFiLazyLoader}
                        imageLoadVerticalOffset={imageLoadVerticalOffset}
                        showQuickViewIcon={showQuickViewIcon}
                        showMoreOptionsBadge={
                            displayMoreOptionsBadge && isMobile && !alignMoreOptionsToBottom
                        }
                        hideDivider={hideDivider}
                        hideProductDetails={hideProductDetails}
                        hideImagePadding={hideImagePadding}
                        {...srcSetProps}
                    />
                    <div
                        className={classnames(styles.info, {
                            [styles.hideDivider]: hideDivider,
                        })}
                    >
                        <Info
                            {...sharedProps}
                            currency={currency}
                            showPrice={showPrice}
                            seller={(seller: Object) || null}
                            showInternalSellerName={showInternalSellerName}
                            showByLine={showByLine}
                            showSellerName={showSellerName}
                            showCreatorName={showCreatorName}
                            showTitle={showTitle}
                            showMoreOptionsBadge={
                                displayMoreOptionsBadge && (!isMobile || alignMoreOptionsToBottom)
                            }
                            moreOptionsTopMargin={alignMoreOptionsToBottom}
                            priceBold={priceBold}
                            titleOneLine={titleOneLine}
                            viewer={viewer}
                        />
                    </div>
                    {showQuickView && showQuickViewIcon && (
                        <QuickViewItem
                            item={(item: Object)}
                            isMobile={isMobile}
                            onClose={closeQuickView}
                            isQuickViewActive={isQuickViewActive}
                        />
                    )}
                </div>
            </TileContextProvider>
        );
    }
}

/* istanbul ignore next */
// $FlowFixMe cant skip istanbul coverage for static properties
TileComponent.defaultProps = {
    currency: 'USD',
    disableLinks: false,
    itemsPerRow: 3,
    showByLine: true,
    showSellerName: true,
    showQuickView: true,
    showQuickViewIcon: true,
    showLightBox: false,
    closeQuickView: () => {},
    openQuickView: () => {},
    useLoFiLazyLoader: false,
    showMoreOptionsBadge: true,
    priceBold: false,
    titleOneLine: false,
    viewer: null,
};

export const Tile = createFragmentContainer(TileComponent, {
    viewer: graphql`
        fragment Tile_viewer on Viewer
            @argumentDefinitions(
                fetchRegionalInfo: { type: "Boolean", defaultValue: false }
                userZipCode: { type: "String", defaultValue: "0" }
                userCountryCode: { type: "String", defaultValue: "" }
            ) {
            ...Info_viewer
                @arguments(
                    fetchRegionalInfo: $fetchRegionalInfo
                    userZipCode: $userZipCode
                    userCountryCode: $userCountryCode
                )
        }
    `,
    item: graphql`
        fragment Tile_item on Item
            @argumentDefinitions(
                isTrade: { type: "Boolean", defaultValue: false }
                showSeller: { type: "Boolean", defaultValue: false }
                showPrice: { type: "Boolean", defaultValue: true }
                pageDisplayEnum: { type: "PageDisplayEnum", defaultValue: searchAndBrowse }
            ) {
            isCustomizable
            isMultiSku
            serviceId
            localizedPdpUrl
            title
            vertical
            ...Info_item
                @arguments(
                    isTrade: $isTrade
                    showPrice: $showPrice
                    pageDisplayEnum: $pageDisplayEnum
                )
            ...Product_item
            ...QuickViewItem_item
            seller @include(if: $showSeller) {
                serviceId
                ...Info_seller
            }
        }
    `,
});
